import { useApi, useSavingsHistoric } from '@/api';
import { ChartSkeleton, ChartTimeFilter } from '@/components';
import { tokensMapping } from '@/utils/tokens';
import {
  CurrencyValue,
  Flex,
  LineChart,
  formatNumber,
  formatPercentage,
  DisplaySwitch
} from '@blockanalitica/ui';
import { useMemo, useState } from 'react';
import { useChartPeriod } from '@/hooks';

export default function HistoricTabs() {
  const [activeDisplayOption, setActiveDisplayOption] = useState('total');
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const [, updateChartConfigWithPeriod] = useChartPeriod(chartDaysAgo);
  const config = updateChartConfigWithPeriod();
  const timeFilter = useMemo(
    () => (
      <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
    ),
    [chartDaysAgo]
  );
  const displayOptions = useMemo(
    () => [
      { key: 'total', value: 'Supply' },
      { key: 'rate', value: 'Savings rate' }
    ],
    []
  );

  const [data, error] = useApi({
    url: '/save/dsr/historic/',
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  if (error) {
    throw error;
  }

  // The total comes from sky-api; the rate from atlas.
  const [savingsRows, savingsError] = useSavingsHistoric(chartDaysAgo);
  const isTotal = activeDisplayOption === 'total';
  const totalRows = useMemo(
    () => savingsRows?.filter((row) => row.total_dsr != null),
    [savingsRows]
  );

  const datasets = useMemo(() => {
    return [
      {
        xField: 'date',
        yField: isTotal ? 'total_dsr' : activeDisplayOption
      }
    ];
  }, [activeDisplayOption, isTotal]);

  const yScaleValueFormatter = (value) => {
    switch (activeDisplayOption) {
      case 'rate':
        return formatPercentage(Number(value));
      default:
        return formatNumber(Number(value));
    }
  };
  const valueFormatter = useMemo(() => {
    function formatValue(value: string) {
      switch (activeDisplayOption) {
        case 'rate':
          return formatPercentage(Number(value));

        default:
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: tokensMapping('DAI'),
                iconSize: '3'
              }}
            />
          );
      }
    }
    return formatValue;
  }, [activeDisplayOption]);

  if (isTotal && savingsError) {
    throw savingsError;
  }

  if (data && (!isTotal || totalRows)) {
    return (
      <Flex flexDirection="column" gap="xs">
        <DisplaySwitch
          data={displayOptions}
          activeOption={activeDisplayOption}
          onChange={setActiveDisplayOption}
        />
        <LineChart
          key={activeDisplayOption}
          data={isTotal ? totalRows : data.historic}
          valueFormatter={valueFormatter}
          datasets={datasets}
          yScaleValueFormatter={yScaleValueFormatter}
          timeFilter={timeFilter}
          config={config}
        />
      </Flex>
    );
  }

  return <ChartSkeleton />;
}
